import { useQueryClient } from "@tanstack/react-query";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Archive,
  ArchiveRestore,
  Eye,
  Images,
  Pencil,
  Plus,
  X,
} from "lucide-react";
import { useState } from "react";

import { useToast } from "@/hooks/use-toast";
import { AppDialog } from "@/components/ui/AppDialog";

import type { TestCase } from "@/api/tests-api";

import { CaseImagesModal } from "./CaseImagesModal";
import { Pagination } from "./Pagination";
import { pageSizeFor } from "@/lib/paging";
import { BULK_DELETE_LIMIT } from "@/api/tests-api";
import { CasePreviewModal } from "./CasePreviewModal";
import { EditCaseModal } from "./EditCaseModal";
import { formatTimestamp } from "./lookups";
import type { BulkCaseAction } from "@/api/tests-api";
import {
  TEST_CASES_KEY,
  useArchiveCase,
  useBulkCaseAction,
  usePublishCase,
  fetchCases,
  useCases,
  useCreateCase,
  useRestoreCase,
} from "@/api/tests-api";
import { TestCaseUploader, summarise } from "./TestCaseUploader";

/**
 * The case library.
 *
 * Phase 1 manages the case RECORD — its title, modality and clinical
 * brief. Uploading the DICOM itself lands in a later phase; the status
 * column already reflects that a case with no images is not yet usable.
 *
 * The de-identification checkbox is not decoration. These are real
 * patients' images about to be shown to a job applicant who does not work
 * here, and the person uploading is the only one who can confirm the file
 * has been cleaned. The backend refuses the create without it.
 */
export function TestLibraryPanel() {
  const [q, setQ] = useState("");
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeFor("cases"));
  const cases = useCases(q.trim() || undefined, showArchived, page, pageSize);
  const rows = cases.data?.items ?? [];
  const total = cases.data?.total ?? 0;
  // HOW MUCH OF THIS PAGE still predates the diagnostic viewer.
  //
  // Scoped to the page on purpose rather than asked of the server: the
  // number is a nudge, not a report, and a second endpoint counting the
  // whole library is a second thing that can disagree with the rows
  // underneath it. Whoever is working through the migration is looking
  // at a page of cases, and this says how many of the ones in front of
  // them are left.
  const unconverted = rows.filter(
    (c) => c.instance_count > 0 && (c.diagnostic_instance_count ?? 0) < c.instance_count,
  ).length;
  // Selected cases, by id, held across pages -- a library this feature
  // exists for is ninety pages long, and a selection that emptied itself
  // on every page turn could never span them.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState<BulkCaseAction | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const bulkCases = useBulkCaseAction();
  const pageIds = rows.map((r) => r.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someOnPage = pageIds.some((id) => selected.has(id));

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const togglePage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPage) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });

  /** Every case the current filter matches, not just the page. */
  const selectAllMatching = async () => {
    setProgress("Finding them all…");
    try {
      const ids: string[] = [];
      for (let p = 1; ; p += 1) {
        const res = await fetchCases(
          q.trim() || undefined,
          showArchived,
          p,
          BULK_DELETE_LIMIT,
        );
        ids.push(...res.items.map((i) => i.id));
        if (res.items.length === 0 || ids.length >= res.total) break;
      }
      setSelected(new Set(ids));
    } catch {
      toast({ title: "Could not read the full list", variant: "error" });
    } finally {
      setProgress(null);
    }
  };

  const runBulk = async (action: BulkCaseAction) => {
    const ids = [...selected];
    setConfirming(null);
    const verb = action === "archive" ? "Archiving" : "Restoring";
    const done = action === "archive" ? "Archived" : "Restored";
    try {
      const result = await bulkCases.mutateAsync({
        action,
        ids,
        onProgress: (n, all) => setProgress(`${verb}… ${n} of ${all}`),
      });
      // The refused stay selected, so it is visible WHICH cases were
      // left alone rather than a number nobody can check.
      setSelected(new Set(result.refused.map((r) => r.id)));
      setPage(1);
      const kept = result.refused.length;
      const reasons = [...new Set(result.refused.map((r) => r.reason))];
      toast({
        title:
          result.affected.length === 0
            ? `Nothing was ${done.toLowerCase()}`
            : `${done} ${result.affected.length} case${result.affected.length === 1 ? "" : "s"}`,
        ...(kept
          ? { description: `${kept} left alone — ${reasons.join("; ")}.` }
          : {}),
        variant: result.affected.length === 0 && kept ? "error" : "success",
      });
    } catch (err) {
      toast({
        title:
          (err as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail ?? `Could not ${action} these cases`,
        variant: "error",
      });
    } finally {
      setProgress(null);
    }
  };

  const create = useCreateCase();
  const archive = useArchiveCase();
  const restore = useRestoreCase();
  const publish = usePublishCase();
  const { toast } = useToast();

  // Which case is being looked at, and which is being edited. Separate,
  // because previewing is a read and editing is not, and an admin often
  // wants to look before deciding to change anything.
  const [previewing, setPreviewing] = useState<TestCase | null>(null);
  const [editing, setEditing] = useState<TestCase | null>(null);
  const [managingImages, setManagingImages] = useState<TestCase | null>(null);
  const [archiving, setArchiving] = useState<TestCase | null>(null);

  const archiveCase = async (row: TestCase) => {
    try {
      await archive.mutateAsync(row.id);
      toast({ title: "Case archived" });
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Could not archive this case";
      toast({ title: detail, variant: "error" });
    }
  };

  const restoreCase = async (row: TestCase) => {
    try {
      await restore.mutateAsync(row.id);
      toast({ title: "Case restored" });
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Could not restore this case";
      toast({ title: detail, variant: "error" });
    }
  };

  const [title, setTitle] = useState("");
  const [modality, setModality] = useState("");
  const [bodyPart, setBodyPart] = useState("");
  const [history, setHistory] = useState("");
  const [deident, setDeident] = useState(false);
  // The case just created, awaiting its files. Held here rather than
  // navigating away: an admin types the brief and drops the study in one
  // continuous action.
  const [pendingCase, setPendingCase] = useState<{ id: string; title: string } | null>(
    null,
  );
  // The New-case dialog. Separate from pendingCase because the dialog
  // stays open ACROSS the create — it swaps to the uploader rather than
  // shutting and making the admin find their way back.
  const [adding, setAdding] = useState(false);
  // Images stored by the upload just finished. Publish needs at least
  // one, and asking the server again for a number we already have would
  // only add a way for the two to disagree.
  const [uploaded, setUploaded] = useState(0);

  // The server's own words when it refuses an incomplete case, held so
  // the admin can read what was lost and decide. Never pre-filled: the
  // first press always goes without the override.
  const [incomplete, setIncomplete] = useState<string | null>(null);

  const publishAndClose = async (acceptIncomplete = false) => {
    if (!pendingCase) return;
    try {
      await publish.mutateAsync({ caseId: pendingCase.id, acceptIncomplete });
      toast({ title: `“${pendingCase.title}” published — it can now be used in an exam` });
      setIncomplete(null);
      closeAdd();
    } catch (err) {
      const res = (err as {
        response?: { status?: number; data?: { detail?: string } };
      })?.response;
      const detail = res?.data?.detail ?? "Could not publish this case";
      // A 422 naming a shortfall is a QUESTION, not an error. Toasting
      // it would put the one sentence that says images are missing into
      // the surface designed to disappear on its own.
      if (res?.status === 422 && detail.includes("could not be stored")) {
        setIncomplete(detail);
        return;
      }
      toast({ title: detail, variant: "error" });
    }
  };

  const closeAdd = () => {
    setAdding(false);
    setUploaded(0);
    setPendingCase(null);
    setTitle("");
    setModality("");
    setBodyPart("");
    setHistory("");
    setDeident(false);
    void cases.refetch();
  };

  const canSubmit = title.trim().length > 0 && deident && !create.isPending;

  const submit = async () => {
    if (!canSubmit) return;
    try {
      const created = await create.mutateAsync({
        title: title.trim(),
        modality: modality.trim() || null,
        body_part: bodyPart.trim() || null,
        clinical_history: history.trim() || null,
        deident_confirmed: true,
      });
      setTitle("");
      setModality("");
      setBodyPart("");
      setHistory("");
      setDeident(false);
      setPendingCase({ id: created.id, title: created.title });
      // The dialog stays open and becomes the uploader.
      toast({ title: "Case created — now add its images" });
    } catch {
      toast({ title: "Could not add the case", variant: "error" });
    }
  };

  return (
    <div data-cy="test-library" className="flex flex-col gap-8">
      {/* Adding a case is a modal, like New exam and New question. It
          keeps the two steps CONTINUOUS though: the form swaps to the
          uploader in place once the case exists, because typing a brief
          and dropping the study is one action in the admin's head, and
          making them navigate back to find an Images button would break
          it. */}
      {adding && (
        <Dialog.Root
          open
          onOpenChange={(next) => {
            if (!next) closeAdd();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
            <Dialog.Content
              data-cy="create-case-modal"
              className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-background-card shadow-theme-lg"
            >
              <div className="flex items-start justify-between gap-3 border-b border-border px-6 pt-6 pb-4">
                <div>
                  <Dialog.Title className="text-lg font-semibold text-foreground-heading">
                    {pendingCase ? `Images — ${pendingCase.title}` : "New case"}
                  </Dialog.Title>
                  <Dialog.Description className="mt-1 text-xs text-foreground-muted">
                    {pendingCase
                      ? "Drop the study folder. Reports and other non-image files are ignored."
                      : "The brief is the only text a candidate reads alongside the images."}
                  </Dialog.Description>
                </div>
                <Dialog.Close
                  data-cy="btn-close-case-form"
                  aria-label="Close"
                  className="rounded-md p-1 text-foreground-muted hover:bg-table-row-hover hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <X className="h-4 w-4" />
                </Dialog.Close>
              </div>

              <div className="px-6 py-5">
                {pendingCase ? (
                  <>
                    <TestCaseUploader
                      caseId={pendingCase.id}
                      onDone={(summary) => {
                        // Invalidate rather than refetch the one query on
                        // screen: a case uploaded while a search is active
                        // may not be in it, and every cached page is now
                        // out of date by exactly this case.
                        void qc.invalidateQueries({ queryKey: TEST_CASES_KEY });
                        void cases.refetch();
                        setUploaded(summary.stored);
                        toast({
                          title: summarise(summary),
                          variant: summary.stored > 0 ? "success" : "error",
                        });
                      }}
                    />
                    {/* Two named outcomes rather than one vague "done".
                        Publishing is the ordinary end of this flow, so it
                        is the filled button; a case still being assembled
                        is saved as a draft and picked up later. */}
                    {/* WHAT WAS LOST, WHERE THE DECISION IS MADE. The
                        server refuses the first press and names the
                        number; this says it in place and offers the two
                        real answers. Re-uploading is written first and
                        is the safe one, so it is the plain button and
                        the one focus lands on -- publishing a study with
                        a hole in it is the choice that has to be made
                        deliberately. */}
                    {incomplete && (
                      <div
                        data-cy="publish-incomplete"
                        className="sk-notice mt-5"
                      >
                        <h3>Some files were not stored</h3>
                        <p>{incomplete}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            data-cy="btn-publish-incomplete-cancel"
                            onClick={() => setIncomplete(null)}
                            className="sk-btn"
                          >
                            Go back and re-upload
                          </button>
                          <button
                            type="button"
                            data-cy="btn-publish-incomplete-accept"
                            disabled={publish.isPending}
                            onClick={() => {
                              void publishAndClose(true);
                            }}
                            className="sk-btn"
                          >
                            Publish it as it is
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-border pt-4">
                      <button
                        type="button"
                        data-cy="btn-publish-case"
                        disabled={uploaded === 0 || publish.isPending}
                        title={
                          uploaded === 0
                            ? "Upload at least one image before publishing"
                            : "Make this case available to exams"
                        }
                        onClick={() => {
                          void publishAndClose();
                        }}
                        className="sk-btn sk-btn-primary"
                      >
                        Publish
                      </button>
                      <button
                        type="button"
                        data-cy="btn-finish-upload"
                        onClick={closeAdd}
                        className="sk-btn"
                      >
                        Save as draft
                      </button>
                      <p className="w-full text-[11px] text-foreground-muted">
                        A draft is kept and can be previewed, but no exam may
                        include it until it is published.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col text-xs text-foreground-muted sm:col-span-3">
            Title
            <input
              data-cy="case-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. CT abdomen — right iliac fossa pain"
              className="sk-input mt-1 w-auto"
            />
          </label>
          <label className="sk-field flex flex-col">
            <span className="sk-label">Modality</span>
            <input
              data-cy="case-modality"
              value={modality}
              onChange={(e) => setModality(e.target.value)}
              placeholder="US / CT / MR"
              className="sk-input mt-1 w-auto"
            />
          </label>
          <label className="flex flex-col text-xs text-foreground-muted sm:col-span-2">
            Body part
            <input
              data-cy="case-body-part"
              value={bodyPart}
              onChange={(e) => setBodyPart(e.target.value)}
              className="sk-input mt-1 w-auto"
            />
          </label>
          <label className="flex flex-col text-xs text-foreground-muted sm:col-span-3">
            Clinical history shown to the candidate
            <textarea
              data-cy="case-history"
              rows={3}
              value={history}
              onChange={(e) => setHistory(e.target.value)}
              className="sk-input mt-1 w-auto"
            />
          </label>
        </div>

        <label className="mt-3 flex items-start gap-2 text-xs text-foreground">
          <input
            type="checkbox"
            data-cy="case-deident"
            checked={deident}
            onChange={(e) => setDeident(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-accent"
          />
          <span>
            I confirm this case has been de-identified. Candidates are not
            employees of the clinic — patient name, ID and date of birth must
            not be present, including burned into the images themselves.
          </span>
        </label>

                    <button
                      type="button"
                      data-cy="btn-add-case"
                      disabled={!canSubmit}
                      onClick={() => {
                        void submit();
                      }}
                      className="sk-btn sk-btn-primary mt-5"
                    >
                      Add case
                    </button>
                  </>
                )}
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}

      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-foreground">
              Library ({total})
            </h3>
            {unconverted > 0 && (
              <p
                data-cy="library-migration-note"
                className="mt-0.5 text-xs text-foreground-muted"
              >
                {/* The originals are discarded at ingest so no
                    identifier ever reaches disk, which is the whole
                    design -- so there is nothing to convert and
                    "migrating the library" means uploading the studies
                    again. Saying that plainly is kinder than a marker
                    nobody can act on. */}
                <span className="font-semibold text-status-wip">
                  {unconverted}
                </span>{" "}
                {unconverted === 1 ? "case on this page" : "cases on this page"}{" "}
                still {unconverted === 1 ? "shows" : "show"} stored JPEGs. Upload
                the study again to window, probe and measure it — there is
                nothing to convert, because the originals are never kept.
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              data-cy="btn-new-case"
              onClick={() => {
                setPendingCase(null);
                setAdding(true);
              }}
              className="sk-btn sk-btn-primary sk-sm"
            >
              <Plus aria-hidden className="h-3.5 w-3.5" />
              Case
            </button>
            <label className="flex items-center gap-2 text-xs text-foreground-muted">
              <input
                type="checkbox"
                data-cy="toggle-archived-cases"
                checked={showArchived}
                onChange={(e) => {
                  setPage(1);
                  setShowArchived(e.target.checked);
                }}
                className="h-4 w-4 accent-accent"
              />
              Show archived
            </label>
            <input
              type="search"
              data-cy="library-search"
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder="Search cases…"
              className="sk-input sk-sm w-56"
            />
          </div>
        </div>

        {rows.length > 0 && (
          <div
            data-cy="case-select-bar"
            className="mb-2 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-background-subtle/50 px-3 py-2"
          >
            <span className="text-xs text-foreground">
              {selected.size === 0 ? "Select" : `${selected.size} selected`}
            </span>

            {total > rows.length && selected.size < total && (
              <button
                type="button"
                data-cy="btn-select-all-cases"
                disabled={progress !== null}
                onClick={() => {
                  void selectAllMatching();
                }}
                className="text-xs font-semibold text-accent hover:underline disabled:opacity-50"
              >
                Select all {total} matching
              </button>
            )}

            {selected.size > 0 && (
              <button
                type="button"
                data-cy="btn-clear-case-selection"
                onClick={() => setSelected(new Set())}
                className="text-xs font-semibold text-foreground-muted hover:underline"
              >
                Clear
              </button>
            )}

            {progress && (
              <span
                data-cy="case-bulk-progress"
                className="text-xs tabular-nums text-foreground-muted"
              >
                {progress}
              </span>
            )}

            {selected.size > 0 && (
              <span className="ml-auto flex items-center gap-2">
                {/* Restore is offered only where archived rows can be
                    seen. Offering it against a list that hides them
                    means a button whose only possible answer is
                    "none of these were archived". */}
                {showArchived && (
                  <button
                    type="button"
                    data-cy="btn-restore-cases"
                    disabled={progress !== null}
                    onClick={() => setConfirming("restore")}
                    className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-table-row-hover disabled:opacity-50"
                  >
                    <ArchiveRestore aria-hidden className="h-3.5 w-3.5" />
                    Restore {selected.size}
                  </button>
                )}
                <button
                  type="button"
                  data-cy="btn-archive-cases"
                  disabled={progress !== null}
                  onClick={() => setConfirming("archive")}
                  className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-table-row-hover disabled:opacity-50"
                >
                  <Archive aria-hidden className="h-3.5 w-3.5" />
                  Archive {selected.size}
                </button>
              </span>
            )}
          </div>
        )}

        {cases.isLoading ? (
          <p className="text-sm text-foreground-muted">Loading…</p>
        ) : rows.length === 0 ? (
          <p data-cy="library-empty" className="text-sm text-foreground-muted">
            No cases yet.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-background-card shadow-theme-sm">
            <table className="w-full min-w-[40rem] text-sm">
              <thead className="border-b-2 border-border-strong bg-table-header text-left text-[12px] font-semibold uppercase tracking-[0.05em] text-table-header-text">
                <tr>
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      data-cy="case-select-page"
                      aria-label="Select every case on this page"
                      checked={allOnPage}
                      ref={(el) => {
                        // Half a page ticked is a real state a plain
                        // checkbox cannot show; without this the header
                        // reads "none selected" while rows below are.
                        if (el) el.indeterminate = someOnPage && !allOnPage;
                      }}
                      onChange={togglePage}
                      className="h-4 w-4 accent-accent"
                    />
                  </th>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Modality</th>
                  <th className="px-4 py-3">Images</th>
                  <th className="px-4 py-3">Used by</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Added</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr
                    key={row.id}
                    data-cy="library-row"
                    className={`border-t border-table-border ${
                      idx % 2 === 0 ? "bg-table-row-bg" : "bg-table-row-alt"
                    } hover:bg-table-row-hover`}
                  >
                    <td className="px-4 py-2.5">
                      <input
                        type="checkbox"
                        data-cy="case-select"
                        aria-label={`Select ${row.title}`}
                        checked={selected.has(row.id)}
                        onChange={() => toggleOne(row.id)}
                        className="h-4 w-4 accent-accent"
                      />
                    </td>
                    <td className="px-4 py-2.5 text-foreground">{row.title}</td>
                    <td className="px-4 py-2.5 text-foreground-muted">
                      {row.modality ?? "—"}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-foreground-muted">
                      {row.instance_count}
                      {/* MARKED WHERE THE COUNT IS, not in a column of
                          its own: this is a property of the images, and
                          a column reading the same thing for every row
                          once the library is migrated earns nothing.
                          It disappears entirely when a case is fully
                          converted, which is the state all of them are
                          heading for. */}
                      {row.instance_count > 0 &&
                        (row.diagnostic_instance_count ?? 0) < row.instance_count && (
                          <span
                            data-cy="needs-reupload"
                            title={
                              (row.diagnostic_instance_count ?? 0) === 0
                                ? "Stored before the diagnostic viewer — re-upload this study to window, probe and measure it"
                                : `Only ${row.diagnostic_instance_count} of ${row.instance_count} images can be read diagnostically — re-upload the study`
                            }
                            className="ml-1.5 rounded border border-status-wip px-1 text-[10px] font-semibold uppercase tracking-wide text-status-wip"
                          >
                            JPEG
                          </span>
                        )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-foreground-muted">
                      {row.exam_count === 0 && row.assigned_count === 0
                        ? "—"
                        : [
                            row.exam_count > 0 &&
                              `${row.exam_count} exam${row.exam_count === 1 ? "" : "s"}`,
                            row.assigned_count > 0 &&
                              `${row.assigned_count} candidate${
                                row.assigned_count === 1 ? "" : "s"
                              }`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                    </td>
                    <td className="px-4 py-2.5 text-foreground-muted">
                      {row.status.toLowerCase()}
                    </td>
                    <td className="px-4 py-2.5 text-foreground-muted">
                      {formatTimestamp(row.created_at)}
                    </td>
                    <td className="px-4 py-2.5">
                      {/* Same shape as the Staff accounts row: bordered
                          pills, right-aligned, destructive colour-coded —
                          one action vocabulary across the admin console. */}
                      {/* Icon-only, because four worded pills crowd a row
                          that already carries five columns. Every one
                          carries aria-label AND title: without them the
                          row is unusable with a screen reader and a
                          guess for anyone who has not seen it before. */}
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          data-cy="btn-preview-case"
                          onClick={() => setPreviewing(row)}
                          disabled={row.instance_count === 0}
                          aria-label={`Preview ${row.title}`}
                          title={
                            row.instance_count === 0
                              ? "No images to preview yet"
                              : "See exactly what a candidate is shown"
                          }
                          className="rounded-md border border-border bg-background-card p-1.5 text-foreground hover:bg-table-row-hover disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Eye aria-hidden className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          data-cy="btn-case-images"
                          onClick={() => setManagingImages(row)}
                          aria-label={`Images for ${row.title} — ${row.instance_count} stored`}
                          title={
                            row.instance_count === 0
                              ? "No images yet — upload the study"
                              : `${row.instance_count} image${row.instance_count === 1 ? "" : "s"} stored`
                          }
                          className="relative rounded-md border border-border bg-background-card p-1.5 text-foreground hover:bg-table-row-hover"
                        >
                          <Images aria-hidden className="h-4 w-4" />
                          {/* No badge at zero. A "0" reads as a count that
                              happens to be low; nothing reads as empty,
                              which is what it is — and the disabled
                              Preview beside it says the same thing.
                              Capped at 99+ so a three-hundred-slice CT
                              does not stretch the cell. */}
                          {row.instance_count > 0 && (
                            <span
                              data-cy="case-image-count"
                              aria-hidden
                              className="absolute -right-1.5 -top-1.5 min-w-[1.1rem] rounded-full bg-accent px-1 text-[10px] font-semibold leading-[1.1rem] text-accent-text"
                            >
                              {row.instance_count > 99 ? "99+" : row.instance_count}
                            </span>
                          )}
                        </button>
                        <button
                          type="button"
                          data-cy="btn-edit-case"
                          onClick={() => setEditing(row)}
                          aria-label={`Edit ${row.title}`}
                          title="Edit the title and clinical history"
                          className="rounded-md border border-border bg-background-card p-1.5 text-foreground hover:bg-table-row-hover"
                        >
                          <Pencil aria-hidden className="h-4 w-4" />
                        </button>
                        {row.status.toUpperCase() === "ARCHIVED" ? (
                          <button
                            type="button"
                            data-cy="btn-restore-case"
                            onClick={() => {
                              void restoreCase(row);
                            }}
                            aria-label={`Restore ${row.title}`}
                            title="Bring this case back into the library"
                            className="rounded-md border border-status-wip/50 bg-status-wip-bg p-1.5 text-status-wip hover:bg-status-wip/20"
                          >
                            <ArchiveRestore aria-hidden className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            data-cy="btn-archive-case"
                            onClick={() => {
                              setArchiving(row);
                            }}
                            aria-label={`Archive ${row.title}`}
                            title="Retire this case from the library"
                            className="rounded-md border border-status-urgent/50 bg-status-urgent-bg p-1.5 text-status-urgent hover:bg-status-urgent/20"
                          >
                            <Archive aria-hidden className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <CasePreviewModal
        testCase={previewing}
        onClose={() => setPreviewing(null)}
      />
      <EditCaseModal testCase={editing} onClose={() => setEditing(null)} />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPage={setPage}
        onPageSize={setPageSize}
        label="cases"
      />

      <CaseImagesModal
        testCase={managingImages}
        onClose={() => setManagingImages(null)}
        onChanged={async () => {
          // AWAIT the refetch and read ITS result. `cases.data` here is
          // the value from the render this callback was created in —
          // i.e. from before the upload — so re-seeding from it showed
          // the modal a count of 0 immediately after storing 21 images.
          const fresh = await cases.refetch();
          setManagingImages((m) =>
            m
              ? (fresh.data?.items ?? []).find((c) => c.id === m.id) ?? m
              : m,
          );
        }}
      />

      {/* "Archive" means something narrower here than most people
          expect, and the count is the part worth reading -- so it is
          stated in the dialog rather than left to a tooltip. */}
      <AppDialog
        open={archiving !== null}
        cy="dialog-archive-case"
        tone="danger"
        title="Archive this case?"
        body={
          archiving
            ? `“${archiving.title}” stops being available to add to new exams.` +
              (archiving.assigned_count > 0
                ? ` ${archiving.assigned_count} candidate${
                    archiving.assigned_count === 1 ? "" : "s"
                  } already hold it and KEEP it — archiving does not take a case off a paper that has been sealed.`
                : " No candidate holds it.")
            : undefined
        }
        onDismiss={() => setArchiving(null)}
        actions={[
          {
            label: "Archive it",
            cy: "btn-confirm-archive-case",
            variant: "danger",
            onClick: () => {
              const row = archiving;
              setArchiving(null);
              if (row) void archiveCase(row);
            },
          },
          {
            label: "Cancel",
            cy: "btn-cancel-archive-case",
            variant: "ghost",
            autoFocus: true,
            onClick: () => setArchiving(null),
          },
        ]}
      />

      {/* The bulk pair. Deliberately NOT tone="danger" like the
          single-case dialog above: that one names one case and can say
          exactly who holds it, which is a specific consequence worth
          alarming about. This one cannot name anything, and dressing a
          reversible action as destructive across a whole selection
          teaches people to click through the warning. */}
      <AppDialog
        open={confirming === "archive"}
        cy="dialog-archive-cases"
        tone="info"
        title={`Archive ${selected.size} case${selected.size === 1 ? "" : "s"}?`}
        body={
          "They stop appearing in the picker, so no new exam can draw " +
          "them, and they cannot be published or uploaded to while " +
          "archived. Nothing is deleted and nothing is taken off a paper " +
          "that has already been sealed — a candidate holding one of " +
          "these keeps it exactly as it is. Restore puts any of them back."
        }
        onDismiss={() => setConfirming(null)}
        actions={[
          {
            label: `Archive ${selected.size}`,
            cy: "btn-confirm-archive-cases",
            variant: "primary",
            autoFocus: true,
            onClick: () => {
              void runBulk("archive");
            },
          },
          {
            label: "Cancel",
            cy: "btn-cancel-archive-cases",
            variant: "ghost",
            onClick: () => setConfirming(null),
          },
        ]}
      />

      <AppDialog
        open={confirming === "restore"}
        cy="dialog-restore-cases"
        tone="info"
        title={`Restore ${selected.size} case${selected.size === 1 ? "" : "s"}?`}
        body={
          "They go back into the picker. Each returns as ready when it " +
          "holds images and as a draft when it does not, rather than to " +
          "whatever it was before — so a restored case describes what is " +
          "actually in it. Anything in the selection that is not archived " +
          "is left alone."
        }
        onDismiss={() => setConfirming(null)}
        actions={[
          {
            label: `Restore ${selected.size}`,
            cy: "btn-confirm-restore-cases",
            variant: "primary",
            autoFocus: true,
            onClick: () => {
              void runBulk("restore");
            },
          },
          {
            label: "Cancel",
            cy: "btn-cancel-restore-cases",
            variant: "ghost",
            onClick: () => setConfirming(null),
          },
        ]}
      />
    </div>
  );
}
